const SORT_STATES = ['asc', 'desc']

export const ENROLLMENTS_COLUMNS = [
  { label: 'Program Name', field: 'program_name' },
  { label: 'Enrollment Status', field: 'enrollment_status' },
  { label: 'Billing Status', field: 'invoice_status' },
  { label: 'Latest Update', field: 'last_update' },
  { label: 'Amount', field: 'amount' },
  { label: 'Payment Method', field: 'payment_method' },
  { label: 'Package', field: 'package' },
  { label: 'Age', field: 'age' },
  { label: 'Level', field: 'level' },
  { label: 'Gender', field: 'gender' },
  // Backend has no date sort column, so this is display-only (non-sortable).
  { label: 'Program Dates', field: 'program_dates', sortable: false },
]

export const DEFAULT_SORT_OPTION = {
  sortField: 'last_update',
  sortValue: 'desc',
}

export const getSortStates = () => SORT_STATES

export const ENROLLMENT_STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Future', value: 'future' },
  { label: 'Waitlist', value: 'waitlisted' },
  { label: 'Completed', value: 'completed' },
  { label: 'Dropped', value: 'dropped' },
]

export const PAYMENT_STATUS_OPTIONS = [
  { label: 'Outstanding', value: 'outstanding' },
  { label: 'Completed', value: 'paid' },
  { label: 'Refunded', value: 'refunded' },
  { label: 'Partially Refunded', value: 'partially_refunded' },
]

export const ENROLLMENT_STATUS_LABELS = {
  active: 'Active',
  future: 'Future',
  completed: 'Completed',
  dropped: 'Dropped',
  pending_approval: 'Pending Approval',
  applied: 'Pending Approval',
  waitlisted: 'Waitlisted',
  pending: 'Pending',
}

export const INVOICE_STATUS_LABELS = {
  outstanding: 'Outstanding',
  paid: 'Paid',
  refunded: 'Refunded',
  partially_refunded: 'Partially Refunded',
}

export const formatInvoiceStatusDisplay = (invoice) => {
  if (!invoice) { return { label: 'No Billing', variant: 'none' } }

  const { status, paymentMethod } = invoice
  const isOnline = paymentMethod === 'online'

  if (status === 'paid') {
    return isOnline
      ? { label: 'Paid - Online', variant: 'paid' }
      : { label: 'Offline Payment', variant: 'offlinePayment' }
  }
  if (status === 'refunded') {
    return isOnline
      ? { label: 'Refunded Online', variant: 'refunded' }
      : { label: 'Refunded Offline', variant: 'refunded' }
  }
  if (status === 'partially_refunded') {
    return { label: 'Partial Refund', variant: 'refunded' }
  }
  return { label: INVOICE_STATUS_LABELS[status] || status, variant: status }
}

// Phase 1 billing status is driven by the enrollment's own payment_status (paid / paid_online /
// invoiced / outstanding) rather than a full invoice. Reuses the invoice badge variants for
// icon + color so no extra styling is needed.
export const formatPaymentStatusDisplay = (paymentStatus) => {
  switch (paymentStatus) {
    case 'paid_online':
      return { label: 'Paid - Online', variant: 'paid' }
    case 'paid':
      return { label: 'Offline Payment', variant: 'offlinePayment' }
    case 'outstanding':
      return { label: 'Outstanding', variant: 'outstanding' }
    case 'invoiced':
      return { label: 'Invoiced', variant: 'invoiced' }
    default:
      return { label: 'No Billing', variant: 'none' }
  }
}

// --- Drawer state helpers ---

export const hasBillingTab = (enrollment) => !!enrollment?.invoice

export const PAYMENT_METHOD_LABELS = {
  online: 'Online - Stripe',
  cash: 'Cash',
  check: 'Check',
  member_charge: 'Member Charge',
  custom: 'Custom',
}

export const formatPaymentMethodDisplay = (invoice) => {
  if (!invoice?.paymentMethod) { return null }
  const { paymentMethod } = invoice
  const isOnline = paymentMethod === 'online'

  if (isOnline) { return 'Online-Stripe' }

  const offlineLabel = PAYMENT_METHOD_LABELS[paymentMethod] || paymentMethod
  return `Offline-${offlineLabel}`
}

export const ACTIVITY_LABELS = {
  created: 'Enrollment Created',
  approved: 'Enrollment Approved',
  package_changed: 'Package Changed',
  payment_status_changed: 'Payment Status Changed',
  course_changed: 'Program Changed',
  completed: 'Enrollment Completed',
  refunded: 'Refund Issued',
  deleted: 'Enrollment Deleted',
  transferred: 'Enrollment Transferred',
  dropped: 'Enrollment Dropped',
  invoice_edited: 'Invoice Edited',
  payment_reminder_sent: 'Payment Reminder Sent',
  payment_received: 'Payment Received',
  marked_as_paid: 'Marked as Paid',
  marked_as_outstanding: 'Marked as Outstanding',
  refund_issued: 'Refund Issued',
}
